import AlertMessage from "../../../../../components/AlertMessage";
import AppTable, { ColumnConfig } from "../../../../../components/AppTable/AppTable";
import ModalGlobal from "../../../../../components/AppModal/ModalGlobal";
import { useMemo, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { SectionPermissionTypeEnum } from "../../../../../types/SectionPermissionTypesEnum";
import { SelectItemDto } from "../../../../../types/SelectItemDto";
import { useTranslation } from "react-i18next";
import { PERMISSIONS } from "../../../../../constants/permissions";
import { getPermissionDescriptionNarrative } from "../../../../../utils/formatters/PermissionsFormatter";
import { RolePermissionsFormDto } from "../../types";
import AppSearchSelect from "../../../../../components/AppSearchSelect/AppSearchSelect";

interface RolePermissionsFormProps {
    selectedPermissions: RolePermissionsFormDto[];
    onAddPermission: (rolePermission: RolePermissionsFormDto) => void;
    onDeletePermission: (rolePermission: RolePermissionsFormDto) => void;
}

export default function RolePermissionsForm({ selectedPermissions, onAddPermission, onDeletePermission }: RolePermissionsFormProps) {
    const [showAddPermissionModal, setShowAddPermissionModal] = useState<boolean>(false);
    const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({});
    const [selectedPermissionOptions, setSelectedPermissionOptions] = useState<SelectItemDto[]>([]);
    const [selectedSectionOptions, setSelectedSectionOptions] = useState<SelectItemDto[]>([]);
    const handleCloseAddPermissionModal = () => {
        setShowAddPermissionModal(false);
        setErrors({});
        setSelectedPermissionOptions([]);
    };

    const addNewPermission = () => {
        setErrors({});
        if (selectedPermissionOptions.length === 0) {
            setErrors({ permission: t("common:form.requiredFieldForm") });
            return;
        }

        selectedPermissionOptions.forEach((selectedOption) => {
            const permission = permissionsByOptionId.get(selectedOption.id) || "";
            const [section, action] = permission.split(".");

            onAddPermission({
                id: 0,
                section: section ?? "", 
                action: action ?? "",
                permission: permission
            });
        });

        handleCloseAddPermissionModal();
    };
    const { t } = useTranslation(['permission', 'common']);

    const columns: ColumnConfig<RolePermissionsFormDto>[] = [
        { header: t("permission:table.section"), key: "section", render: (item) => t(`permission:sections.${item.section}`) ?? ""},
        { header: t("permission:table.name"), key: "name", render: (item) => getPermissionDescriptionNarrative(item.section ?? "", item.action ?? "") },
        { header: t("permission:table.action"), key: "action", render: (item) => t(`permission:actions.${item.action}`) ?? "" }
    ];
    const permissionOptionsRaw = Object.values(PERMISSIONS).flatMap((actions) =>
        Object.values(actions)
            .filter(value => {
                const [permissionSection, permissionAction] = value.split('.');
                return !selectedPermissions.some(
                    selectedPermission =>
                        selectedPermission.section === permissionSection &&
                        selectedPermission.action === permissionAction
                );
            })
            .map(value => {
                const [permissionSection, permissionAction] = value.split('.');
                return {
                    value: value,
                    label: `${t(`permission:sections.${permissionSection}`)} - ${t(`permission:actions.${permissionAction}`)}`
                };
            })
    );
    const sectionOptionsRaw = [
        ...new Set(
            Object.values(PERMISSIONS)
                .flatMap(actions => Object.values(actions))
                .map(value => value.split(".")[0])
        )
    ]
        .map(permissionSection => ({
            value: t(`permission:sections.${permissionSection}`),
            label: t(`permission:sections.${permissionSection}`)
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const permissionOptions: SelectItemDto[] = permissionOptionsRaw.map((option, index) => ({
        id: index + 1,
        name: option.label
    }));

    const sectionOptions: SelectItemDto[] = sectionOptionsRaw.map((option, index) => ({
        id: index + 1,
        name: option.label
    }));

    const permissionsByOptionId = new Map<number, string>(
        permissionOptionsRaw.map((option, index) => [index + 1, option.value])
    );

    const filteredTableData = useMemo(() => {
        const activeLabels = selectedSectionOptions.map((s) => s.name);

        return selectedPermissions.filter((item) => {
            if (item.dateDelete != null) return false;
            if (activeLabels.length === 0) return true;

            const itemSectionLabel = item.section ? t(`permission:sections.${item.section}`) : "";
            return activeLabels.includes(itemSectionLabel);
        });
    }, [selectedPermissions, selectedSectionOptions, t]);

    return (
      <>
          <ModalGlobal
                show={showAddPermissionModal}
                onClose={handleCloseAddPermissionModal}
                title={t("permission:modal.permissionDescriptionModal")}
                onSave={addNewPermission}
                body={
                    <Form>
                        {!!errors["alreadyExist"] && <Row>
                            <Col xs={12} md={12}>
                                <AlertMessage
                                    message={errors["alreadyExist"]}
                                    type={"danger"}
                                />
                            </Col>
                        </Row>}
                        <Row>
                            <Col xs={12} md={12} >
                                <Form.Group>
                                    <Form.Label>{t("permission:modal.permissionTitleModal")}</Form.Label>
                                    <AppSearchSelect
                                        isMulti={true}
                                        value={selectedPermissionOptions}
                                        options={permissionOptions}
                                        placeholder={t('common:search')}
                                        onChange={(selectedOption) => {
                                            if (!selectedOption) {
                                                setSelectedPermissionOptions([]);
                                                return;
                                            }
                                            setSelectedPermissionOptions(
                                                Array.isArray(selectedOption) ? selectedOption : [selectedOption]
                                            );
                                        }}
                                    />
                                    <Form.Control.Feedback type="invalid" style={{ display: "block" }}>
                                        {errors["permission"]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
              }
              saveText={t("common:buttons.buttonAccept")}
              closeText={t("common:buttons.buttonCancel")}
              isLoading={false}
          />
            <Row style={{ marginTop: '40px' }} className="align-items-center">
                <Col>
                    <h5>{t("permission:headerRolePermissionsTable")}</h5>
                </Col>

                <Col xs="auto">
                    <div className="d-flex gap-2 align-items-center">
                        <div style={{ width: '250px' }}>
                            <AppSearchSelect
                                isMulti={true}
                                value={selectedSectionOptions}
                                options={sectionOptions}
                                placeholder={t('common:search')}
                                onChange={(selectedOption) => {
                                    if (!selectedOption) {
                                        setSelectedSectionOptions([]);
                                        return;
                                    }

                                    setSelectedSectionOptions(
                                        Array.isArray(selectedOption) ? selectedOption : [selectedOption]
                                    );
                                }}
                            />
                        </div>

                        <Button onClick={() => setShowAddPermissionModal(true)}>
                            {t("permission:buttonNewPermission")}
                        </Button>
                    </div>
                </Col>
            </Row>
            <AppTable<RolePermissionsFormDto>
                data={filteredTableData}
                columns={columns}
                emptyMessage={t("permission:nonPermissions")}
                t={t}
                hasActions={true}
                onDelete={ onDeletePermission }
                hasPagination={false}
                sectionPermission={SectionPermissionTypeEnum.ROLES}
            />
      </>
  );
}

